import { RichTextEditor } from "@/components/ui/rich-text-editor"
import { containsLocalImageUrls } from "@/utils/richTextImages"
import { Button, Stack, Text } from "@chakra-ui/react"
import { useState } from "react"

interface FeedbackSubmitFormProps {
  initialValue?: string
  onSubmit: (
    feedbackText: string,
    isPublished?: boolean,
  ) => void | Promise<void>
  onCancel?: () => void
  isSubmitting?: boolean
  submitLabel?: string
  showPublishButton?: boolean
}

export function FeedbackSubmitForm({
  initialValue = "",
  onSubmit,
  onCancel,
  isSubmitting = false,
  submitLabel = "Submit Feedback",
  showPublishButton = false,
}: FeedbackSubmitFormProps) {
  const [feedbackText, setFeedbackText] = useState(initialValue)
  const [error, setError] = useState<string | null>(null)
  const [isUploadingImage, setIsUploadingImage] = useState(false)

  const handleSubmit = async (e: React.FormEvent, isPublished = false) => {
    e.preventDefault()
    setError(null)

    if (isUploadingImage || containsLocalImageUrls(feedbackText)) {
      setError("Wait for images to finish uploading before saving.")
      return
    }

    // Validate feedback text
    const textContent = feedbackText.replace(/<[^>]*>/g, "").trim()
    if (!textContent) {
      setError("Feedback cannot be empty")
      return
    }

    try {
      await onSubmit(feedbackText, isPublished)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit feedback")
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap={4}>
        <RichTextEditor
          value={feedbackText}
          onChange={setFeedbackText}
          onUploadStateChange={setIsUploadingImage}
          onUploadError={setError}
          placeholder="Describe your feedback or question about this problem..."
          minHeight="200px"
        />

        {error && (
          <Text color="red.500" fontSize="sm">
            {error}
          </Text>
        )}

        <Stack direction="row" gap={2} justify="flex-end">
          {onCancel && (
            <Button
              variant="outline"
              onClick={onCancel}
              disabled={isSubmitting || isUploadingImage}
            >
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            colorPalette="blue"
            loading={isSubmitting || isUploadingImage}
            disabled={isSubmitting || isUploadingImage}
            onClick={(e) => handleSubmit(e, false)}
          >
            {submitLabel}
          </Button>
          {showPublishButton && (
            <Button
              type="button"
              colorPalette="green"
              loading={isSubmitting || isUploadingImage}
              disabled={isSubmitting || isUploadingImage}
              onClick={(e) => handleSubmit(e, true)}
            >
              Publish Reply (Visible to All)
            </Button>
          )}
        </Stack>
      </Stack>
    </form>
  )
}
